import { Injectable } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { BaseFormControls } from '../../forms/form-controls/base.form-controls';
import { BaseForm } from '../../forms/forms/base.form';
import { DashboardProductForm } from '../../forms/forms/dashboard-product.form';
import { ProductWithBarcode } from '../../shared/models/product-with-barcode.model';
import { SelectedProductState } from '../../shared/models/selected-product-state.model';
import { convertProductReferenceToProductOption } from '../../shared/utils/product.utils';
import { BarcodeSearchResult, Product, ProductReference } from '../api/api.entities';
import { BarcodesApi } from '../api/barcodes/barcodes-api.service';
import { ProductsApi } from '../api/products/products-api.service';

@Injectable({
  providedIn: 'root',
})
export abstract class SelectedProductService {
  abstract get dependingForms(): BaseForm<BaseFormControls>[];
  abstract get productForm(): DashboardProductForm;

  product?: ProductWithBarcode;
  productChangeListener?: Subscription;
  state = new SelectedProductState();
  submission: Subject<Product | undefined> = new Subject<Product | undefined>();

  constructor(
    protected barcodesApi: BarcodesApi,
    protected productsApi: ProductsApi
  ) {}

  clearSelectedProduct(): void {
    this.clearForms(true);
    this.product = undefined;
    this.state.isLoading = false;
  }

  protected clearForms(clearProduct: boolean = false): void {
    if (clearProduct) {
      this.productForm.clear();
    }
  }

  findProductByBarcode(barcode: string): void {
    this.disableForms();
    this.state.isLoadingByBarcode = true;

    this.barcodesApi.findBarcodeProduct(barcode).subscribe(({ match }: BarcodeSearchResult) => {
      if (!match) {
        this.clearForms();
        this.product = undefined;
        this.submission.next(undefined);
        this.state.isSubmitting = false;

        setTimeout(() => {
          this.state.isLoading = false;
          this.enableForms();
        }, 500);

        return;
      }

      const product: ProductWithBarcode = { ...match.product, barcode: match.barcode };

      this.productForm.controls.product.setValue(
        this.productForm.options.products.find(p => p.id === product.id)?.name || product.detailedName
      );

      this.productForm.options.products = [];
      this.productForm.barcodeExists = true;

      this.loadSelectedProduct(product);
    });
  }

  findProductByName(productId: number): void {
    if (this.productForm.barcodeExists) {
      this.productForm.controls.barcode.setValue(null);
    }

    this.disableForms();
    this.state.isLoadingByProductId = true;

    this.productsApi.findOne(productId).subscribe((product: Product) => {
      this.loadSelectedProduct(product);
    });
  }

  loadSelectedProduct(product: ProductWithBarcode): void {
    this.product = product;

    if (!this.productForm.controls.product.value) {
      this.productForm.controls.product.setValue(
        this.productForm.options.products.find(p => p.id === product.id)?.name || product.detailedName
      );
    }

    if (this.state.isSubmitting) {
      this.submission.next(product);
      this.state.isSubmitting = false;
    }

    setTimeout(() => {
      this.state.isLoading = false;
      this.enableForms();
    }, 500);
  }

  setBarcodeChangeListener(): void {
    this.productForm.controls.barcode.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(barcode => {
        const { barcode: barcodeControl, product: productControl } = this.productForm.controls;

        if (this.state.isLoading || (barcodeControl.untouched && productControl.touched)) return;

        if (barcode) {
          this.findProductByBarcode(barcode);
        } else this.clearSelectedProduct();
      });
  }

  setProductChangeListener(products: ProductReference[]): void {
    if (this.productChangeListener) {
      this.productChangeListener.unsubscribe();
    }

    this.productChangeListener = this.productForm.controls.product.valueChanges
      .pipe(debounceTime(300))
      .subscribe(name => {
        const product = this.product;
        const { barcode: barcodeControl, product: productControl } = this.productForm.controls;

        if (
          this.state.isLoading ||
          (barcodeControl.value && productControl.untouched) ||
          name === product?.detailedName
        )
          return;

        if (!name) {
          this.productForm.options.products = products.map(convertProductReferenceToProductOption);

          for (const form of this.dependingForms) {
            form.clear();
          }

          if (this.productForm.barcodeExists) {
            this.productForm.barcodeExists = false;
            this.productForm.controls.barcode.setValue('');
          }
        } else {
          this.productForm.options.products = products
            .filter(p => p.name.toLowerCase().includes(name.toLowerCase()))
            .map(convertProductReferenceToProductOption);

          const exactMatches = this.productForm.options.products.filter(
            p => p.name.toLowerCase() === name.toLowerCase()
          );

          if (exactMatches.length === 1) {
            const product = exactMatches[0];
            this.findProductByName(product.id);
          }
        }
      });
  }

  protected disableForms(): void {
    this.productForm.disable();
  }

  protected enableForms(): void {
    this.productForm.enable();
  }
}
